import React, { useEffect, useMemo, useState } from 'react'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import SendingFormsTable from '@components/pages/FormBuilder/SendingFormsTable'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import { EmptyState } from '@enamelai/design-system'
import type {
  SendingAppointmentDetail,
  SendingFormEntry,
} from '@components/pages/FormBuilder/SendingFormsTable/SendingFormsTable.component'
import type { PendingFormEntry } from '@pages/FormBuilder/assignedForms.types'
import {
  getAppointmentsSummary,
  type AppointmentSummary,
} from '@services/api/appointment.api'
import { getAppointmentLocalDateYmd } from '@utils/time.util'

const SEND_FORMS_END_DATE_DAYS = 365

const GLOBAL_LOADING_CONTAINER = 'body'

let cachedSendFormAppointments: {
  clinicId: string
  appointments: AppointmentSummary[]
} | null = null

function currentClinicId(): string {
  return localStorage.getItem('clinic_id') ?? ''
}

function readCachedAppointments(): AppointmentSummary[] | null {
  if (
    cachedSendFormAppointments == null ||
    cachedSendFormAppointments.clinicId !== currentClinicId()
  ) {
    return null
  }
  return cachedSendFormAppointments.appointments
}

function isAppointmentInFuture(apt: AppointmentSummary): boolean {
  const startStr =
    apt.appointment_start_date ?? `${apt.appt_date} ${apt.appt_start}`
  const start = new Date(startStr.replace(' ', 'T'))
  return start >= new Date()
}

function appointmentToSendingDetail(
  apt: AppointmentSummary,
): SendingAppointmentDetail {
  return {
    appointmentId: apt.appointment_id,
    appointmentType: apt.appointmenttype?.name ?? '—',
    roomNo: apt.operatory_id ? `Room ${apt.operatory_id.slice(0, 8)}` : '—',
    appointmentDate: getAppointmentLocalDateYmd(apt),
    lastReminded: '',
  }
}

function groupAppointmentsByPatient(
  appointments: AppointmentSummary[],
): SendingFormEntry[] {
  const future = appointments.filter(isAppointmentInFuture)
  const byPatient = new Map<
    string,
    {
      patientName: string
      publicPatientId: string
      patientMobile?: string
      appointments: SendingAppointmentDetail[]
    }
  >()

  for (const apt of future) {
    const id = apt.patient_id
    const name =
      apt.patient != null
        ? `${apt.patient.first_name ?? ''} ${apt.patient.last_name ?? ''}`.trim() ||
          '—'
        : '—'
    const publicPatientId = apt.patient?.public_id || '—'
    const patientMobile = apt.patient?.cellphone ?? ''

    if (!byPatient.has(id)) {
      byPatient.set(id, {
        patientName: name,
        publicPatientId,
        patientMobile,
        appointments: [],
      })
    }
    byPatient.get(id)!.appointments.push(appointmentToSendingDetail(apt))
  }

  return Array.from(byPatient.entries()).map(
    ([
      patientId,
      {
        patientName,
        publicPatientId,
        patientMobile,
        appointments: patientAppointments,
      },
    ]) => ({
      patientId,
      patientName,
      publicPatientId,
      patientMobile,
      numberOfAppointments: patientAppointments.length,
      appointments: patientAppointments,
    }),
  )
}

function SendFormsTabEmpty({
  hasNoRecords,
  onCreateForm,
}: {
  hasNoRecords: boolean
  onCreateForm?: () => void
}): React.ReactElement {
  return (
    <EmptyState
      className="form-builder-empty"
      iconClass="mgc mgc_document_fill"
      title="No forms found"
      description={
        hasNoRecords
          ? "You don't have any forms to send yet."
          : 'No forms match your search.'
      }
      actionLabel={hasNoRecords ? 'Create form' : undefined}
      onAction={hasNoRecords ? onCreateForm : undefined}
    />
  )
}

interface SendFormsTabProps {
  selectedPatientId?: string
  searchQuery: string
  onFormsAssigned?: (entry: PendingFormEntry) => void
  onCreateForm?: () => void
  onEmptyRecordsChange?: (isEmpty: boolean) => void
  onLoadingChange?: (isLoading: boolean) => void
  suppressGlobalLoading?: boolean
}

const SendFormsTab: React.FC<SendFormsTabProps> = ({
  selectedPatientId,
  searchQuery,
  onFormsAssigned,
  onCreateForm,
  onEmptyRecordsChange,
  onLoadingChange,
  suppressGlobalLoading = false,
}) => {
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const hasCachedAppointments = readCachedAppointments() !== null
  const [rawAppointments, setRawAppointments] = useState<AppointmentSummary[]>(
    () => readCachedAppointments() ?? [],
  )
  const [loading, setLoading] = useState<boolean>(!hasCachedAppointments)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const hasCache = readCachedAppointments() !== null
    if (!hasCache) {
      setLoading(true)
      setError(null)
      if (!suppressGlobalLoading) {
        showLoading({ container: GLOBAL_LOADING_CONTAINER })
      }
    }
    const today = new Date()
    const start = today.toISOString().slice(0, 10)
    const end = new Date(today)
    end.setDate(end.getDate() + SEND_FORMS_END_DATE_DAYS)
    const endStr = end.toISOString().slice(0, 10)

    getAppointmentsSummary(start, endStr)
      .then((res) => {
        if (cancelled) return
        const list = res?.data?.appointments ?? []
        cachedSendFormAppointments = {
          clinicId: currentClinicId(),
          appointments: list,
        }
        setRawAppointments(list)
        setError(null)
      })
      .catch(() => {
        if (cancelled || hasCache) return
        setError('Failed to load upcoming appointments.')
        setRawAppointments([])
        showToast(
          createFailedToast(
            'Error',
            'Failed to load upcoming appointments. Please try again.',
          ),
        )
      })
      .finally(() => {
        if (cancelled) return
        if (!hasCache && !suppressGlobalLoading) {
          hideLoading(GLOBAL_LOADING_CONTAINER)
        }
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [hideLoading, showLoading, showToast, suppressGlobalLoading])

  const sendingData = useMemo(
    () => groupAppointmentsByPatient(rawAppointments),
    [rawAppointments],
  )

  const filteredData = useMemo(
    () =>
      sendingData.filter((entry) => {
        if (selectedPatientId && entry.patientId !== selectedPatientId) {
          return false
        }
        const q = searchQuery.trim().toLowerCase()
        if (!q) return true
        return (
          entry.patientName.toLowerCase().includes(q) ||
          entry.publicPatientId.toLowerCase().includes(q)
        )
      }),
    [searchQuery, selectedPatientId, sendingData],
  )

  const scopedCount = sendingData.filter((entry) =>
    selectedPatientId ? entry.patientId === selectedPatientId : true,
  ).length
  const hasNoRecords = !loading && !error && scopedCount === 0

  useEffect(() => {
    onLoadingChange?.(loading)
    return () => onLoadingChange?.(false)
  }, [loading, onLoadingChange])

  useEffect(() => {
    onEmptyRecordsChange?.(hasNoRecords)
    return () => onEmptyRecordsChange?.(false)
  }, [hasNoRecords, onEmptyRecordsChange])

  if (loading) {
    return null
  }

  if (error) {
    return (
      <div className="form-builder-error" role="alert">
        {error}
      </div>
    )
  }

  if (filteredData.length === 0) {
    return (
      <SendFormsTabEmpty
        hasNoRecords={scopedCount === 0}
        onCreateForm={onCreateForm}
      />
    )
  }

  return (
    <SendingFormsTable data={filteredData} onFormsAssigned={onFormsAssigned} />
  )
}

export default SendFormsTab
